import { markChatThreadsAsRead } from "@circle-react/api/chatThreadsApi";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useLocation } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { QueryObserverResult } from "react-query";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatThreadMessage } from "@/react/types/Chat/ChatRoomMessage";

import { isElementInViewPort } from "./helper";
import { useCommunityMemberThreadsChannelWebSocket } from "./useCommunityMemberThreadsChannelWebSocket";

interface UnreadChatThreadsContextType {
  fetchUnreadChatThreads: () => Promise<QueryObserverResult<any, any>>;
  isLoading: boolean;
  isUnreadThreadFlow: boolean;
  newBadgeIds: Set<number>;
  onEventReceive: (eventData: any) => void;
  resetNewBadgeIds: () => void;
  unreadChatThreadIds: number[];
  unreadThreadsCount: number;
}

const UnreadChatThreadsContext =
  createContext<UnreadChatThreadsContextType | null>(null);
UnreadChatThreadsContext.displayName = "UnreadChatThreadsContext";

interface UnreadChatThreadsResponse {
  chat_thread_ids: number[];
}

export const UnreadChatThreadsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentCommunityMember } = usePunditUserContext();
  const currentMemberId = currentCommunityMember?.id;

  const [chatThreadIds, setChatThreadIds] = useState<number[]>([]);
  const [isUnreadThreadFlow, setUnreadThreadFlow] = useState<boolean>(false);
  const [newBadgeIds, setNewBadgeIds] = useState<Set<number>>(new Set());

  const pathname = useLocation({ select: l => l.pathname });
  const isThreadsPage =
    pathname === "/threads" ||
    pathname.startsWith("/settings/inbox/chat-threads");
  const activeUnifiedInboxChatThreadId = useMemo(() => {
    const match = /^\/settings\/inbox\/chat-threads\/(\d+)/.exec(pathname);
    return match ? Number(match[1]) : null;
  }, [pathname]);

  const onChatThreadRead = useCallback((readThreadId: number) => {
    setChatThreadIds(prev =>
      prev.includes(readThreadId)
        ? prev.filter(threadId => threadId !== readThreadId)
        : prev,
    );
  }, []);

  const onNewMessage = useCallback(
    (message: ChatThreadMessage) => {
      if (message?.unread_flow) {
        setUnreadThreadFlow(true);
      }

      const isSenderCurrentCommunityMember =
        message.sender?.community_member_id === currentMemberId;
      const isNotThreadMessage = !message.chat_thread_id;
      const isLiveRoomMessage = message?.chat_room_parent_type === "Live::Room";

      if (
        isNotThreadMessage ||
        isSenderCurrentCommunityMember ||
        isLiveRoomMessage
      )
        return;

      if (
        activeUnifiedInboxChatThreadId &&
        message.chat_thread_id === activeUnifiedInboxChatThreadId
      ) {
        void markChatThreadsAsRead(message.chat_thread_id);
        return;
      }

      setChatThreadIds(prev => {
        if (prev.includes(message.chat_thread_id)) return prev;

        if (isThreadsPage) {
          const threadElement = document.getElementById(
            String(message.chat_thread_id),
          );
          if (threadElement && isElementInViewPort(threadElement)) return prev;
        }

        return [...prev, message.chat_thread_id];
      });
    },
    [activeUnifiedInboxChatThreadId, currentMemberId, isThreadsPage],
  );

  const onEventReceive = useCallback(
    (eventData: any) => {
      if (eventData.event === "chatThreadRead") {
        onChatThreadRead(eventData.chat_thread_id);
      } else if (eventData.event === "newMessage") {
        onNewMessage(eventData.json_message);
      }
    },
    [onChatThreadRead, onNewMessage],
  );

  useCommunityMemberThreadsChannelWebSocket({
    communityMemberId: currentMemberId,
    onMessageReceive: onEventReceive,
    deps: [onEventReceive],
  });

  const {
    isInitialLoading,
    refetch: fetchUnreadChatThreads,
    data,
  } = useQuery<UnreadChatThreadsResponse>({
    queryKey: ["thread_messages", "unread", currentMemberId],
    queryFn: () => reactQueryGet(internalApi.chatThreads.unreadChatThreads()),
    enabled: !!currentCommunityMember?.policies?.can_access_threads_tab,
    meta: { errorMessage: t("request_failure_message") },
  });

  useEffect(() => {
    if (data?.chat_thread_ids) {
      setChatThreadIds(data.chat_thread_ids);
    }
  }, [data]);

  useEffect(() => {
    setNewBadgeIds(prev => new Set([...prev, ...chatThreadIds]));
  }, [chatThreadIds]);

  const resetNewBadgeIds = useCallback(() => {
    setNewBadgeIds(new Set());
  }, []);

  const value = useMemo<UnreadChatThreadsContextType>(
    () => ({
      fetchUnreadChatThreads,
      unreadChatThreadIds: chatThreadIds,
      unreadThreadsCount: chatThreadIds.length,
      isLoading: isInitialLoading,
      onEventReceive,
      isUnreadThreadFlow,
      newBadgeIds,
      resetNewBadgeIds,
    }),
    [
      fetchUnreadChatThreads,
      chatThreadIds,
      isInitialLoading,
      onEventReceive,
      isUnreadThreadFlow,
      newBadgeIds,
      resetNewBadgeIds,
    ],
  );

  return (
    <UnreadChatThreadsContext.Provider value={value}>
      {children}
    </UnreadChatThreadsContext.Provider>
  );
};

export const useUnreadChatThreads = (): UnreadChatThreadsContextType => {
  const context = useContext(UnreadChatThreadsContext);
  if (!context) {
    throw new Error(
      "useUnreadChatThreads must be used within UnreadChatThreadsProvider",
    );
  }
  return context;
};
